import { lazy, Suspense, useState } from 'react';
import { MapPin, X } from 'lucide-react';

const DEFAULT_CENTER: [number, number] = [46.0685, 23.5745];

const LocationPickerMap = lazy(() => import('./LocationPickerMap'));

interface LocationPickerProps {
  value: { lng: number; lat: number } | null;
  onChange: (value: { lng: number; lat: number } | null) => void;
}

function parseLatLng(input: string): { lat: number; lng: number } | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(',').map((s) => s.trim());
  if (parts.length !== 2) return null;
  const lat = parseFloat(parts[0]);
  const lng = parseFloat(parts[1]);
  if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

export function LocationPicker({ value, onChange }: LocationPickerProps) {
  const center: [number, number] = value ? [value.lat, value.lng] : DEFAULT_CENTER;
  const loading = <div className="flex h-full items-center justify-center bg-gray-50 text-sm text-muted-foreground">Se încarcă harta...</div>;
  const [coordsInput, setCoordsInput] = useState('');
  const [coordsError, setCoordsError] = useState(false);

  function handleCoordsApply() {
    const parsed = parseLatLng(coordsInput);
    if (parsed) {
      onChange(parsed);
      setCoordsInput('');
      setCoordsError(false);
    } else {
      setCoordsError(true);
    }
  }

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <input
          type="text"
          value={coordsInput}
          onChange={(e) => { setCoordsInput(e.target.value); setCoordsError(false); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCoordsApply(); } }}
          placeholder="Lipește coordonate din Google Maps (ex: 46.0685, 23.5745)"
          className={`w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20 ${coordsError ? 'border-red-400' : ''}`}
        />
        <button
          type="button"
          onClick={handleCoordsApply}
          disabled={!coordsInput.trim()}
          className="shrink-0 rounded-lg bg-[var(--vivid-purple)] px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--vivid-purple)]/90 disabled:opacity-40"
        >
          Aplică
        </button>
      </div>
      {coordsError && (
        <p className="mb-2 text-xs text-red-500">Format invalid. Folosește „latitudine, longitudine" (ex: 46.0685, 23.5745).</p>
      )}
      <div className="overflow-hidden rounded-xl border" style={{ height: 240 }}>
        <Suspense fallback={loading}>
          <LocationPickerMap center={center} value={value} onChange={onChange} />
        </Suspense>
      </div>
      <div className="mt-2 flex items-center justify-between">
        {value ? (
          <>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" />
              {value.lat.toFixed(5)}, {value.lng.toFixed(5)}
            </span>
            <button type="button" onClick={() => onChange(null)} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700">
              <X className="h-3 w-3" /> Șterge locația
            </button>
          </>
        ) : (
          <span className="text-xs text-muted-foreground">Click pe hartă pentru a alege locația</span>
        )}
      </div>
    </div>
  );
}
